import { useEffect, useRef, useState } from 'react';

type LazyLoadProps = {
  children: JSX.Element;
  threshold: number;
  rootMargin: string;
  placeholderClassname: string;
  minHeight: number | string;
};

/**
 * LazyLoad component that lazy loads children
 * @param {Object} props
 * @param {ReactElement} props.children - Component children that will be lazy loaded
 * @param {number} props.threshold - Indicates what percentage of the target's visibility the observer's callback should be executed
 * @param {string} props.rootMargin - Margin around the root
 * @param {string} props.placeholderClassname - placeholder class name (before lazy loading happens)
 * @param {number|string} props.minHeight - placeholder div min-height
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API for more info on
 * `rootMargin` and `threshold`
 */
const LazyLoad = ({ children, threshold, rootMargin, placeholderClassname, minHeight }: LazyLoadProps): JSX.Element => {
  // React refs
  const observer = useRef<IntersectionObserver | null>(null);
  const el = useRef<HTMLDivElement | null>(null);

  // React state
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin,
      threshold,
    };

    if (window) {
      observer.current = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          if (el.current && observer.current) {
            observer.current.unobserve(el.current);
          }
          setIsVisible(true);
        }
      }, options);

      // Start observing
      if (el.current) {
        observer.current.observe(el.current);
      }
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return isVisible ? children : <div style={{ minHeight }} ref={el} className={placeholderClassname} />;
};

LazyLoad.defaultProps = {
  threshold: 0.0,
  rootMargin: '50px',
  placeholderClassname: '',
};

export default LazyLoad;
